import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useAuth } from "../context/authContext";
import { useNavigate } from "react-router-dom";
import { confirmAndLogout } from "../utils/session";
import { ordenarFranjas } from "../utils/franjas";
import CampanaNotificaciones from "../components/CampanaNotificaciones";
import AvisoActivarNotificaciones from "../components/AvisoActivarNotificaciones";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import jsPDF from "jspdf";

const Comprador = () => {
  const [compras, setCompras] = useState([]);
  const [compradores, setCompradores] = useState([]);
  const [menuAbiertoId, setMenuAbiertoId] = useState(null);
  const [menuPos, setMenuPos] = useState({ id: null, left: 0, top: 0 });
  const [toast, setToast] = useState({ visible: false, message: '', type: 'info' });
  const toastTimerRef = useRef(null);
  const [confirmData, setConfirmData] = useState({ open: false, compra: null, target: null });
  const [ordenFecha, setOrdenFecha] = useState('none'); // 'asc' | 'desc' | 'none'

  // Estado de descarga
  const [descargando, setDescargando] = useState(false);
  const [progreso, setProgreso] = useState({ actual: 0, total: 0 });
  // null | { url, nombre } — cuando el PDF ya está listo para que el usuario lo pulse
  const [pdfListo, setPdfListo] = useState(null);

  // Toast helper
  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  const showToast = (message, type = 'info', timeout = 2500) => {
    setToast({ visible: true, message, type });
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast({ visible: false, message: '', type: 'info' }), timeout);
  };

  const [busqueda, setBusqueda] = useState("");
  const [filtroRuta, setFiltroRuta] = useState("");
  const [franjaFiltro, setFranjaFiltro] = useState("Todas");

  const [franjasDisponibles, setFranjasDisponibles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { user } = useAuth();
  const usuario = user?.user || user;
  const navigate = useNavigate();
  const [notifsEnabled, setNotifsEnabled] = useState(false);

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem('enComprador_notifs');
      setNotifsEnabled(stored === '1');
    } catch (_) {}
  }, []);

  const handleToggleNotifs = () => {
    const next = !notifsEnabled;
    const pregunta = next ? '🔔 ¿Habilitar notificaciones? 🔔' : '🔕 ¿Deshabilitar notificaciones? 🔕';
    const confirmar = window.confirm(pregunta);
    if (!confirmar) return;
    try { sessionStorage.setItem('enComprador_notifs', next ? '1' : '0'); setNotifsEnabled(next); } catch (_) { setNotifsEnabled(s=>!s); }
  };

  const handleLogout = () => {
    confirmAndLogout(navigate, '/login');
  };

  const fetchCompras = async (usuario) => {
    setLoading(true);
    try {
      const res = await axios.get(`/api/compras?user_comprador=${encodeURIComponent(usuario)}`);
      const lista = Array.isArray(res.data) ? res.data : [];
      setCompras(lista);
      const franjas = Array.from(new Set(lista.map((c) => c.franja_horaria))).filter(Boolean);
      setFranjasDisponibles(ordenarFranjas(franjas));
      setLoading(false);
    } catch (err) {
      setError('❌ Error al cargar las compras del comprador');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!usuario) return;
    fetchCompras(usuario);
    fetchCompradores();
  }, [usuario]);

  useEffect(() => {
    const handlePopState = () => {
      const didLogout = confirmAndLogout(navigate, '/login');
      if (!didLogout) {
        try { window.history.pushState(null, "", window.location.href); } catch(_) {}
      }
    };
    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [navigate]);

  const openConfirm = (compra, target) => {
    setConfirmData({ open: true, compra, target });
    setMenuAbiertoId(null);
  };
  const closeConfirm = () => setConfirmData({ open: false, compra: null, target: null });

  const fetchCompradores = async () => {
    try {
      const res = await axios.get(`/api/compras/compradores`);
      const lista = Array.isArray(res.data?.compradores) ? res.data.compradores : [];
      setCompradores(lista);
    } catch (e) {
      console.warn('No se pudieron cargar los compradores', e);
    }
  };

  const irADetalleCompra = (compra) => {
    navigate("/comprador/detalleCompra", {
      state: {
        id: compra.id,
        id_piso: compra.id_piso,
        productos: compra.productos,
        abreviatura: compra.abreviatura_piso,
        precio_total: compra.precio_total,
        notas_supervisora: compra.notas_supervisora,
        user_supervisora: compra.user_supervisora,
      },
    });
  };

  const coloresRutas = [
    "#4ECDC4",
    "#4CAF50",
    "#556270",
    "#C7F464",
    "#C44DFF",
    "#FFB347"
  ];

  const obtenerColorRuta = (ruta) => {
    const index = rutasUnicas.indexOf(ruta) % coloresRutas.length;
    return coloresRutas[index];
  };

  const comprasFiltradas = compras.filter((c) => {
    const coincideBusqueda = c.abreviatura_piso.toLowerCase().includes(busqueda.toLowerCase());
    const coincideRuta = filtroRuta === "" || c.ruta?.toString() === filtroRuta;
    const coincideFranja = franjaFiltro === "Todas" || c.franja_horaria === franjaFiltro;
    return coincideBusqueda && coincideRuta && coincideFranja;
  });

  const rutasUnicas = [...new Set(comprasFiltradas.map((c) => c.ruta))];

  const parseFecha = (f) => {
    try {
      if (!f) return 0;
      if (typeof f === 'string' && /\d{1,2}\/\d{1,2}\/\d{2,4}/.test(f)) {
        const [d, m, y] = f.split('/').map(Number);
        return new Date(y < 100 ? 2000 + y : y, (m || 1) - 1, d || 1).getTime();
      }
      return new Date(f).getTime();
    } catch { return 0; }
  };

  const comprasOrdenadas = (() => {
    if (ordenFecha === 'none') return comprasFiltradas;
    const arr = [...comprasFiltradas];
    arr.sort((a, b) => {
      const ta = parseFecha(a.dia_pedido);
      const tb = parseFecha(b.dia_pedido);
      return ordenFecha === 'asc' ? (ta - tb) : (tb - ta);
    });
    return arr;
  })();

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const { source, destination } = result;
    const updatedCompras = Array.from(compras);
    const comprasFiltradasIndices = compras
      .map((c, i) => ({ ...c, originalIndex: i }))
      .filter((c) => franjaFiltro === "Todas" || c.franja_horaria === franjaFiltro);
    const movedItem = comprasFiltradasIndices[source.index];
    const fromIndex = movedItem.originalIndex;
    updatedCompras.splice(fromIndex, 1);
    const targetList = compras
      .filter((c) => franjaFiltro === "Todas" || c.franja_horaria === franjaFiltro)
      .map((c) => c.id);
    const toIndex = compras.findIndex((c) => c.id === targetList[destination.index]);
    updatedCompras.splice(toIndex, 0, movedItem);
    setCompras(updatedCompras);
    localStorage.setItem(`orden_compras_${usuario}`, JSON.stringify(updatedCompras.map((c) => c.id)));
  };

  const reasignarCompra = async (id_compra, nuevo) => {
    try {
      await axios.post(`/api/compras/reasignar_comprador`, { id_compra, nuevo_comprador: nuevo });
      setCompras(prev => prev.filter(c => c.id !== id_compra));
      try {
        const key = `orden_compras_${usuario}`;
        const orden = JSON.parse(localStorage.getItem(key) || '[]');
        localStorage.setItem(key, JSON.stringify(Array.isArray(orden) ? orden.filter(id => id !== id_compra) : []));
      } catch (_) {}
      setMenuAbiertoId(null);
      showToast(`✅ Compra reasignada a ${nuevo}`, 'success');
    } catch (e) {
      showToast('❌ No se pudo reasignar la compra', 'error');
      fetchCompras(usuario);
    }
  };

  // ===== Utilidades de descarga PDF =====

  // Cede el control al navegador para que pinte el overlay antes de bloquear el hilo
  const cederUI = () => new Promise((r) => requestAnimationFrame(() => setTimeout(r, 30)));

  // Detecta iOS / iPadOS
  const esIOS = () => {
    const ua = navigator.userAgent || '';
    return /iPad|iPhone|iPod/.test(ua) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  };

  // Detecta navegadores embebidos (Instagram, WhatsApp, Facebook…) que bloquean descargas
  const esNavegadorInApp = () =>
    /(FBAN|FBAV|Instagram|Line|Twitter|WhatsApp|TikTok)/i.test(navigator.userAgent || '');

  // Cierra el overlay de "PDF listo" y libera la URL del blob
  const cerrarPdfListo = () => {
    if (pdfListo?.url) URL.revokeObjectURL(pdfListo.url);
    setPdfListo(null);
    setDescargando(false);
  };

  // Pinta los datos de una compra en el documento jsPDF
  const pintarCompraEnDoc = (doc, compra) => {
    const pageHeight = 297;
    const marginTop = 20;
    const lineHeight = 10;
    const bottomMargin = 20;
    const maxY = pageHeight - bottomMargin;

    let y = marginTop;
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');

    const abreviatura_piso_sin_cajetin = (compra.abreviatura_piso || '').split('(')[0];
    doc.text(`Piso ID: ${compra.id_piso} - ${abreviatura_piso_sin_cajetin}`, 10, y);
    y += 15;

    doc.setFontSize(14);
    const fechaPedido = compra.dia_pedido || '';
    doc.text(`Fecha pedido: ${fechaPedido}`, 10, y); y += lineHeight;
    doc.text(`Franja horaria: ${compra.franja_horaria || '-'}`, 10, y); y += lineHeight;
    if (compra.user_supervisora) { doc.text(`Supervisora: ${compra.user_supervisora}`, 10, y); y += lineHeight; }

    if (compra.notas_supervisora && String(compra.notas_supervisora).trim() !== '') {
      y += 4;
      doc.text('Notas de la supervisora:', 10, y);
      y += 8;
      const maxWidth = 180;
      const paddingH = 5;
      const paddingW = 5;
      const notasWrapped = doc.splitTextToSize(String(compra.notas_supervisora), maxWidth - paddingW * 2);
      const blockHeight = notasWrapped.length * lineHeight + paddingH * 2;
      if (y + blockHeight > maxY) { doc.addPage(); y = marginTop; }
      doc.rect(10, y, maxWidth, blockHeight);
      doc.setFont('helvetica', 'normal');
      let textY = y + paddingH + 2;
      notasWrapped.forEach((linea) => {
        if (textY > maxY) { doc.addPage(); textY = marginTop; }
        doc.text(linea, 10 + paddingW, textY);
        textY += lineHeight;
      });
      y += blockHeight + 6;
    } else {
      doc.setFont('helvetica', 'normal');
    }

    y += lineHeight;
    (compra.productos || []).slice().sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''))
      .forEach((p) => {
        if (y > maxY) { doc.addPage(); y = marginTop; }
        doc.text(`- ${p.nombre} (Cantidad: ${p.cantidad})`, 10, y);
        y += lineHeight;
      });
  };

  // Genera el PDF individual y lo deja listo para que el usuario pulse el enlace
  const descargarCompraPDF = async (compra) => {
    if (esNavegadorInApp()) {
      showToast('Abre la app en Safari o Chrome para descargar', 'info', 4000);
      return;
    }
    setProgreso({ actual: 0, total: 1 });
    setDescargando(true);
    await cederUI();
    try {
      const doc = new jsPDF();
      pintarCompraEnDoc(doc, compra);
      const fechaFormateada = String(compra.dia_pedido || '').replace(/\//g, '-').replace(/[: ]/g, '_');
      const nombre = `Compra_${compra.id}_${compra.abreviatura_piso}_${fechaFormateada}.pdf`;
      const url = URL.createObjectURL(doc.output('blob'));
      setProgreso({ actual: 1, total: 1 });
      setPdfListo({ url, nombre });
    } catch (e) {
      showToast('❌ No se pudo generar el PDF', 'error');
      setDescargando(false);
    }
  };

  // Genera un único PDF con todas las compras (1 página por compra) y lo deja listo
  const descargarTodosPDF = async () => {
    const lista = comprasOrdenadas;
    if (lista.length === 0) return;
    if (esNavegadorInApp()) {
      showToast('Abre la app en Safari o Chrome para descargar', 'info', 4000);
      return;
    }
    setProgreso({ actual: 0, total: lista.length });
    setDescargando(true);
    await cederUI();
    try {
      const doc = new jsPDF();
      for (let i = 0; i < lista.length; i++) {
        if (i > 0) doc.addPage();
        pintarCompraEnDoc(doc, lista[i]);
        setProgreso({ actual: i + 1, total: lista.length });
        await cederUI();
      }
      const nombre = `Compras_${usuario}_${new Date().toISOString().slice(0, 10)}.pdf`;
      const url = URL.createObjectURL(doc.output('blob'));
      setPdfListo({ url, nombre });
    } catch (e) {
      showToast('❌ No se pudieron generar los PDF', 'error');
      setDescargando(false);
    }
  };


  if (loading) return <p>Cargando compras...</p>;
  if (error) return <p>{error}</p>;

  return (
    <div
      style={{
        maxWidth: "700px",
        margin: "0 auto",
        padding: "20px",
        fontSize: "18px",
        position: "relative",
      }}
    >
      <div style={{ position: 'absolute', top: 12, left: 12, right: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 1000 }}>
        <button
          onClick={handleLogout}
          style={{
            padding: '12px 16px',
            borderRadius: 10,
            backgroundColor: '#ef4444',
            color: '#fff',
            border: 'none',
            cursor: 'pointer',
            fontWeight: 800,
            fontSize: 15,
          }}
          title="Cerrar sesión"
        >
          Cerrar sesión
        </button>
        <button
          onClick={() => navigate("/comprador/ordenarCompra")}
          style={{
            padding: '12px 16px',
            borderRadius: 10,
            backgroundColor: '#7c3aed',
            color: '#fff',
            border: 'none',
            cursor: 'pointer',
            fontWeight: 800,
            fontSize: 15,
          }}
          title="Elegir en qué orden quieres ver los productos al hacer la compra"
        >
          🔀 Ordenar compra
        </button>
      </div>

      {/* Overlay: generando PDF o PDF ya listo */}
      {(descargando || pdfListo) && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 2000,
          background: 'rgba(17,24,39,0.55)', backdropFilter: 'blur(3px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            background: '#fff', borderRadius: 18, padding: '28px 32px',
            minWidth: 280, maxWidth: '88vw',
            boxShadow: '0 20px 50px rgba(0,0,0,0.35)', textAlign: 'center'
          }}>
            {!pdfListo ? (
              /* Spinner mientras genera */
              <>
                <div style={{
                  width: 64, height: 64, margin: '0 auto 16px', borderRadius: '50%',
                  border: '6px solid #e0e7ff', borderTopColor: '#4b6bff',
                  animation: 'spinPdf 0.8s linear infinite'
                }} />
                <div style={{ fontSize: 20, fontWeight: 800, color: '#111827', marginBottom: 6 }}>
                  Generando PDF…
                </div>
                {progreso.total > 1 && (
                  <>
                    <div style={{ fontSize: 15, color: '#6b7280', marginBottom: 12 }}>
                      {progreso.actual} de {progreso.total}
                    </div>
                    <div style={{ height: 10, borderRadius: 999, background: '#eef2ff', overflow: 'hidden' }}>
                      <div style={{
                        height: '100%',
                        width: `${progreso.total ? (progreso.actual / progreso.total) * 100 : 0}%`,
                        background: 'linear-gradient(90deg,#6c86ff,#4b6bff)',
                        transition: 'width .2s ease'
                      }} />
                    </div>
                  </>
                )}
                <div style={{ fontSize: 28, marginTop: 12 }}>📄</div>
              </>
            ) : (
              /* PDF listo: el usuario pulsa el enlace real */
              <>
                <div style={{ fontSize: 44, marginBottom: 6 }}>✅</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#111827', marginBottom: 4 }}>
                  ¡PDF listo!
                </div>
                <div style={{
                  fontSize: 13, color: '#6b7280', marginBottom: 20,
                  wordBreak: 'break-all', maxWidth: 260, margin: '0 auto 20px'
                }}>
                  {pdfListo.nombre}
                </div>
                {/*
                  Enlace real que el usuario toca directamente.
                  - iOS: sin 'download', con target="_blank" → abre visor de Safari donde puede guardar
                  - Android/PC: con 'download' → descarga directa
                  Nunca usamos a.click() programático, que es lo que fallaba.
                */}
                <a
                  href={pdfListo.url}
                  download={esIOS() ? undefined : pdfListo.nombre}
                  target={esIOS() ? '_blank' : undefined}
                  rel="noopener noreferrer"
                  onClick={() => setTimeout(cerrarPdfListo, 1500)}
                  style={{
                    display: 'inline-block',
                    background: 'linear-gradient(135deg,#4b6bff,#6c86ff)',
                    color: '#fff',
                    padding: '14px 28px',
                    borderRadius: 12,
                    fontSize: 18,
                    fontWeight: 800,
                    textDecoration: 'none',
                    boxShadow: '0 8px 20px rgba(75,107,255,0.35)',
                    marginBottom: 14
                  }}
                >
                  {esIOS() ? '📂 Abrir PDF' : '📥 Descargar PDF'}
                </a>
                <br />
                <button
                  onClick={cerrarPdfListo}
                  style={{
                    background: '#f3f4f6', border: '1px solid #e5e7eb',
                    padding: '8px 18px', borderRadius: 10, cursor: 'pointer',
                    fontWeight: 700, color: '#374151', fontSize: 15
                  }}
                >
                  Cancelar
                </button>
              </>
            )}
          </div>
          <style>{`@keyframes spinPdf { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {toast.visible && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <div
            style={{
              background: toast.type === 'success' ? '#e8f5e9' : toast.type === 'error' ? '#fdecea' : '#eef2ff',
              color: toast.type === 'success' ? '#1b5e20' : toast.type === 'error' ? '#7f1d1d' : '#1e3a8a',
              border: '1px solid rgba(0,0,0,0.08)',
              boxShadow: '0 16px 36px rgba(0,0,0,0.25)',
              padding: '16px 22px',
              borderRadius: 14,
              fontSize: 18,
              fontWeight: 700,
              maxWidth: 520,
              textAlign: 'center',
            }}
            role="status"
            aria-live="polite"
          >
            {toast.message}
          </div>
        </div>
      )}

      {/* Confirm modal for reassignment */}
      {confirmData.open && (
        <div
          onClick={closeConfirm}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 14,
              padding: '18px 20px',
              width: 'min(500px, 92vw)',
              maxWidth: '92vw',
              boxShadow: '0 16px 36px rgba(0,0,0,0.3)'
            }}
            role="dialog"
            aria-modal="true"
          >
            <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 10, color: '#111827', wordBreak: 'break-word' }}>Confirmar reasignación</div>
            <div style={{ fontSize: 16, color: '#374151', lineHeight: 1.5, wordBreak: 'break-word' }}>
              ¿Quieres asignar la compra
              <span style={{ display: 'inline-block', background: '#eef2ff', color: '#1e3a8a', padding: '2px 8px', borderRadius: 8, margin: '0 6px', fontWeight: 800 }}>
                #{confirmData.compra?.id}
              </span>
              -
              <span style={{ display: 'inline-block', background: '#ecfeff', color: '#155e75', padding: '2px 8px', borderRadius: 8, margin: '0 6px', fontWeight: 800 }}>
                {confirmData.compra?.abreviatura_piso}
              </span>
              a
              <span style={{ display: 'inline-block', background: '#dcfce7', color: '#166534', padding: '2px 8px', borderRadius: 8, margin: '0 6px', fontWeight: 800 }}>
                {confirmData.target}
              </span>
              ?
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
              <button onClick={closeConfirm} style={{ background: '#f3f4f6', border: '1px solid #e5e7eb', padding: '10px 14px', borderRadius: 10, cursor: 'pointer', fontWeight: 700 }}>Cancelar</button>
              <button
                onClick={() => { const idc = confirmData.compra?.id; const tgt = confirmData.target; closeConfirm(); if (idc && tgt) reasignarCompra(idc, tgt); }}
                style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)', color: '#fff', border: 'none', padding: '10px 16px', borderRadius: 10, cursor: 'pointer', fontWeight: 800, boxShadow: '0 6px 14px rgba(22,163,74,0.35)' }}
              >
                Confirmar ✓
              </button>
            </div>
          </div>
        </div>
      )}

      {usuario && (
        <div style={{ marginTop: 64 }}>
          <AvisoActivarNotificaciones user={usuario} />
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, marginTop: "16px", marginBottom: "20px" }}>
        <h2 style={{ textAlign: "center", fontSize: "24px", margin: 0 }}>
          Panel de comprador de{" "}
          <strong style={{ color: "#007bff" }}>{usuario}</strong>
        </h2>
        <CampanaNotificaciones rol="comprador" usuario={usuario} conActivarPush />
      </div>

      <div style={{ display: "flex", justifyContent: "center" }}>
        <button
          onClick={() => navigate("/comprador/verCompras")}
          style={{
            background: "#1976d2",
            color: "#fff",
            border: "none",
            marginBottom: "20px",
            padding: "14px 28px",
            borderRadius: 10,
            cursor: "pointer",
            fontSize: 18,
            fontWeight: "bold",
            boxShadow: "0 6px 14px rgba(25,118,210,0.4)",
          }}
          title="Ver panel de control"
        >
          📜 Panel de control
        </button>
      </div>

      {!loading && (
        <div style={{ marginBottom: "20px", textAlign: "center" }}>
          <input
            type="text"
            placeholder="Buscar abreviatura..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            style={{
              width: "200px",
              padding: "12px 14px",
              fontSize: "18px",
              border: "1px solid #ccc",
              borderRadius: "8px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
          />
          <select
            value={filtroRuta}
            onChange={(e) => setFiltroRuta(e.target.value)}
            style={{
              width: "150px",
              padding: "12px 14px",
              fontSize: "18px",
              border: "1px solid #ccc",
              borderRadius: "8px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              cursor: "pointer",
              textAlign: "center",
            }}
          >
            <option value="">Rutas (todas)</option>
            {rutasUnicas.sort((a, b) => a - b).map((ruta) => (
              <option
                key={ruta}
                value={ruta}
                style={{
                  backgroundColor: obtenerColorRuta(ruta),
                  color: "#fff",
                  textAlign: "center",
                }}
              >
                Ruta {ruta}
              </option>
            ))}
          </select>
          <select
            value={franjaFiltro}
            onChange={(e) => setFranjaFiltro(e.target.value)}
            style={{
              width: "150px",
              padding: "12px 14px",
              fontSize: "18px",
              border: "1px solid #ccc",
              borderRadius: "8px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              cursor: "pointer",
              textAlign: "center",
            }}
          >
            <option value="Todas">Franjas (todas)</option>
            {franjasDisponibles.map((franja) => (
              <option key={franja} value={franja}>
                {franja}
              </option>
            ))}
          </select>
          {(busqueda !== "" || filtroRuta !== "" || franjaFiltro !== "Todas") && (
            <button
              onClick={() => { setBusqueda(""); setFiltroRuta(""); setFranjaFiltro("Todas"); }}
              style={{
                marginLeft: 8,
                padding: "8px 12px",
                fontSize: 13,
                fontWeight: 700,
                borderRadius: 8,
                border: "1.5px solid #dc2626",
                background: "#fee2e2",
                color: "#991b1b",
                cursor: "pointer",
              }}
              title="Quitar todos los filtros"
            >
              ✕ Limpiar filtros
            </button>
          )}
          <button
            onClick={() => setOrdenFecha(prev => prev === 'none' ? 'asc' : prev === 'asc' ? 'desc' : 'none')}
            style={{
              marginLeft: 12,
              padding: '14px 18px',
              fontSize: 18,
              borderRadius: 12,
              border: '1px solid #e5e7eb',
              background: ordenFecha === 'asc'
                ? 'linear-gradient(135deg,#3b82f6,#2563eb)'
                : ordenFecha === 'desc'
                ? 'linear-gradient(135deg,#a78bfa,#7c3aed)'
                : 'linear-gradient(135deg,#fef3c7,#fde68a)',
              color: ordenFecha === 'none' ? '#78350f' : '#fff',
              cursor: 'pointer',
              boxShadow: ordenFecha === 'none' ? '0 4px 10px rgba(120,53,15,0.18)' : '0 6px 14px rgba(37,99,235,0.30)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              transition: 'transform .08s ease, box-shadow .08s ease'
            }}
            onMouseOver={(e) => { e.currentTarget.style.transform = 'scale(1.02)'; }}
            onMouseOut={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
            title="Ordenar por fecha de pedido"
            aria-label="Ordenar por fecha de pedido"
          >
            <span role="img" aria-hidden="true">📅</span>
            {ordenFecha === 'none' && <span>Ordenar por fecha</span>}
            {ordenFecha === 'asc' && <span>Fecha ↑ (asc)</span>}
            {ordenFecha === 'desc' && <span>Fecha ↓ (desc)</span>}
          </button>
          <div style={{ marginTop: 10, display: 'flex', justifyContent: 'center' }}>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 12px',
                borderRadius: 9999,
                background: 'linear-gradient(135deg,#ecfeff,#e0f2fe)',
                color: '#155e75',
                fontWeight: 800,
                border: '1px solid #e5e7eb',
                boxShadow: '0 4px 10px rgba(0,0,0,0.08)'
              }}
              aria-live="polite"
            >
              <span role="img" aria-hidden="true">🛒</span>
              <span>Compras:</span>
              <span style={{ display: 'inline-block', background: '#fff', color: '#111827', padding: '2px 8px', borderRadius: 999, border: '1px solid #e5e7eb' }}>
                {comprasOrdenadas.length}
              </span>
            </div>
          </div>
        </div>
      )}

      {comprasOrdenadas.length === 0 ? (
        <p style={{ textAlign: "center", marginTop: "30px" }}>
          No hay compras en estado <strong>pedido</strong> en esta franja.
        </p>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="comprasDroppable">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {comprasOrdenadas.map((compra, index) => (
                  <Draggable key={compra.id} draggableId={compra.id.toString()} index={index}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={{
                          userSelect: "none",
                          padding: 15,
                          marginBottom: 15,
                          borderRadius: 8,
                          backgroundColor: snapshot.isDragging ? "#d1e7dd" : "#f7f7f7",
                          border: "1px solid #ccc",
                          fontSize: "18px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          position: 'relative',
                          cursor: snapshot.isDragging ? "grabbing" : "grab",
                          ...provided.draggableProps.style,
                        }}
                        aria-label="Arrastrar para reordenar"
                      >
                        <span
                          {...provided.dragHandleProps}
                          style={{ fontSize: "22px", cursor: "grab", marginRight: "12px", userSelect: "none" }}
                          title="Arrastrar para reordenar"
                        >
                          ☰
                        </span>
                        <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
                          <span style={{ fontSize: "24px", fontWeight: "bold" }}>
                            {compra.abreviatura_piso}
                          </span>
                          <span>{compra.franja_horaria}</span>
                          <span><strong>{compra.precio_total.toFixed(2)}</strong> €</span>
                          <span
                            style={{
                              backgroundColor: obtenerColorRuta(compra.ruta),
                              color: "#fff",
                              padding: "4px 10px",
                              borderRadius: "6px",
                              fontWeight: "bold",
                              minWidth: "60px",
                              textAlign: "center",
                              display: "inline-block"
                            }}
                          >
                            Ruta: {compra.ruta}
                          </span>
                          <span><strong>{new Date(compra.dia_pedido).toLocaleDateString("es-ES", {
                            day: "2-digit", month: "short", year: "numeric"
                          })}</strong></span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={() => descargarCompraPDF(compra)}
                              disabled={descargando || !!pdfListo}
                              style={{
                                backgroundColor: '#1976d2',
                                border: 'none',
                                color: 'white',
                                padding: '10px 16px',
                                borderRadius: '6px',
                                cursor: (descargando || pdfListo) ? 'not-allowed' : 'pointer',
                                opacity: (descargando || pdfListo) ? 0.6 : 1,
                                fontSize: '14px'
                              }}
                              title="Descargar PDF"
                              aria-label="Descargar PDF"
                            >
                              📥
                            </button>
                            <button
                              onClick={() => irADetalleCompra(compra)}
                              style={{
                                backgroundColor: '#28a745',
                                border: 'none',
                                color: 'white',
                                padding: '10px 16px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '14px'
                              }}
                              title="Ver detalles"
                            >
                              ➡️
                            </button>
                          </div>

                          <div style={{ position: 'relative' }}>
                            <button
                              onClick={(e) => {
                                if (menuAbiertoId === compra.id) {
                                  setMenuAbiertoId(null); setMenuPos({ id: null, left: 0, top: 0 });
                                  return;
                                }
                                const rect = e.currentTarget.getBoundingClientRect();
                                const menuW = 300;
                                const menuH = 340;
                                const margin = 10;
                                const vw = window.innerWidth; const vh = window.innerHeight;
                                const abreHaciaArriba = (vh - rect.bottom) < (menuH + margin);
                                const left = Math.min(vw - menuW - 8, Math.max(8, rect.right - menuW));
                                const top = abreHaciaArriba ? Math.max(8, rect.top - menuH - margin) : Math.min(vh - 8, rect.bottom + margin);
                                setMenuPos({ id: compra.id, left, top });
                                setMenuAbiertoId(compra.id);
                              }}
                              style={{
                                background: 'linear-gradient(135deg, #6c86ff, #4b6bff)',
                                border: 'none',
                                color: 'white',
                                padding: '14px 16px',
                                borderRadius: '12px',
                                cursor: 'pointer',
                                fontSize: '22px',
                                boxShadow: '0 8px 18px rgba(75,107,255,0.35)'
                              }}
                              title="Reasignar"
                              aria-haspopup="menu"
                              aria-expanded={menuAbiertoId === compra.id}
                            >
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                                <path d="M7 7h8a4 4 0 0 1 0 8h-1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M9 5l-2 2 2 2" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M17 17H9a4 4 0 0 1 0-8h1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M15 19l2-2-2-2" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                            {menuAbiertoId === compra.id && (
                              <div
                                role="menu"
                                style={{
                                  position: 'fixed',
                                  left: menuPos.left,
                                  top: menuPos.top,
                                  background: '#ffffff',
                                  border: '1px solid rgba(0,0,0,0.08)',
                                  borderRadius: 16,
                                  boxShadow: '0 14px 36px rgba(0,0,0,0.22)',
                                  minWidth: 300,
                                  zIndex: 10,
                                  padding: 0,
                                  overflow: 'hidden'
                                }}
                              >
                                <div style={{ padding: '12px 14px', fontWeight: 800, fontSize: 16, color: '#111827', background: 'linear-gradient(135deg, #eef2ff, #e0e7ff)', borderBottom: '1px solid #e5e7eb' }}>
                                  Elegir comprador
                                </div>
                                <div style={{ maxHeight: 320, overflowY: 'auto', padding: '8px 10px' }}>
                                  {compradores
                                    .filter(cmp => cmp && cmp !== usuario)
                                    .map((cmp) => (
                                      <button
                                        key={cmp}
                                        onClick={() => openConfirm(compra, cmp)}
                                        role="menuitem"
                                        style={{
                                          width: '100%',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 12,
                                          textAlign: 'left',
                                          background: '#ffffff',
                                          border: '1px solid #e5e7eb',
                                          padding: '12px 14px',
                                          cursor: 'pointer',
                                          fontSize: 16,
                                          borderRadius: 12,
                                          marginBottom: 10,
                                          color: '#111827',
                                          boxShadow: '0 2px 6px rgba(0,0,0,0.04)'
                                        }}
                                      >
                                        <span style={{
                                          width: 28, height: 28, borderRadius: '50%',
                                          background: '#eef2ff', color: '#4f46e5',
                                          display: 'inline-flex', alignItems: 'center',
                                          justifyContent: 'center', fontWeight: 800
                                        }}>
                                          {String(cmp).slice(0, 2).toUpperCase()}
                                        </span>
                                        <span>{cmp}</span>
                                      </button>
                                    ))}
                                  {compradores.filter(cmp => cmp && cmp !== usuario).length === 0 && (
                                    <div style={{ padding: '10px 12px', color: '#6b7280', fontSize: 14 }}>
                                      No hay otros compradores
                                    </div>
                                  )}
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '10px 12px', borderTop: '1px solid #e5e7eb', background: '#fafafa' }}>
                                  <button
                                    onClick={() => { setMenuAbiertoId(null); setMenuPos({ id: null, left: 0, top: 0 }); }}
                                    style={{
                                      background: '#f3f4f6', border: '1px solid #e5e7eb',
                                      padding: '8px 12px', borderRadius: 10,
                                      cursor: 'pointer', fontWeight: 700, color: '#374151'
                                    }}
                                  >
                                    Cancelar
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      {comprasFiltradas.length > 0 && (
        <button
          onClick={descargarTodosPDF}
          disabled={descargando || !!pdfListo}
          style={{
            position: "fixed",
            bottom: "20px",
            left: "20px",
            backgroundColor: "#007bff",
            color: "white",
            border: "none",
            padding: "12px 20px",
            borderRadius: "6px",
            cursor: (descargando || pdfListo) ? "not-allowed" : "pointer",
            opacity: (descargando || pdfListo) ? 0.6 : 1,
            fontSize: "16px",
          }}
        >
          Descargar 📥
        </button>
      )}
    </div>
  );
};

export default Comprador;
